import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import NewsletterPopup from './components/NewsletterPopup';
import Navbar from './components/Navbar';

import Hero from './components/Hero';
import CropShowcase from './components/CropShowcase'; 
import SulfurBurners from './components/SulfurBurners';
import Hydrocoolers from './components/Hydrocoolers';
import DPADrenchers from './components/DPADrenchers';
import IrrigationFiltration from './components/IrrigationFiltration';
import Footer from './components/Footer';
import DealerLogos from './components/DealerLogos';
import HubSpotTracking from './components/HubSpotTracking';
import { useAttributionPageView } from './hooks/useAttributionPageView';
import SulfurBurnerGuidePage from './pages/SulfurBurnerGuidePage';
import AcidCostCalculatorPage from './pages/AcidCostCalculatorPage';
import BlueberryPage from './pages/BlueberryPage';
import AlmondPage from './pages/AlmondPage';
import CherryPage from './pages/CherryPage';
import CannabisPage from './pages/CannabisPage';
import ApricotPage from './pages/ApricotPage';
import ArtichokePage from './pages/ArtichokePage';
import AsparagusPage from './pages/AsparagusPage';
import ApplePage from './pages/ApplePage';
import GrapePage from './pages/GrapePage';
import PistachioPage from './pages/PistachioPage';
import HopsPage from './pages/HopsPage';
import RaspberryPage from './pages/RaspberryPage';
import TomatoPage from './pages/TomatoPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CherryHydrocoolerPage from './pages/CherryHydrocoolerPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SulfurBurnersPage from './pages/SulfurBurnersPage';
import HydrocoolersPage from './pages/HydrocoolersPage';
import PortableHydrocoolersPage from './pages/PortableHydrocoolersPage';
import DPADrenchersPage from './pages/DPADrenchersPage';
import IrrigationFiltrationPage from './pages/IrrigationFiltrationPage';
import FiltrationLandingPage from './pages/FiltrationLandingPage';
import InlineFiltersPage from './pages/InlineFiltersPage';
import BIGSeriesPage from './pages/BIGSeriesPage';
import BIGSSeriesPage from './pages/BIGSSeriesPage';
import GNGSeriesPage from './pages/GNGSeriesPage';
import BrushCleaningPage from './pages/BrushCleaningPage';
import VacuumCleaningPage from './pages/VacuumCleaningPage';
import TIGSeriesPage from './pages/TIGSeriesPage';
import CleaningOptionsPage from './pages/CleaningOptionsPage';
import ManualCleaningPage from './pages/ManualCleaningPage';
import SemiAutoCleaningPage from './pages/SemiAutoCleaningPage';
import FullyAutomaticCleaningPage from './pages/FullyAutomaticCleaningPage';
import SelfCleaningFiltersPage from './pages/SelfCleaningFiltersPage';
import SuctionScreensPage from './pages/SuctionScreensPage';
import StandardSuctionScreensPage from './pages/StandardSuctionScreensPage';
import SelfCleaningSuctionScreensPage from './pages/SelfCleaningSuctionScreensPage';
import PassiveFishScreensPage from './pages/PassiveFishScreensPage';
import WaterIntakeLandingPage from './pages/WaterIntakeLandingPage';
import WaterBoxPage from './pages/WaterBoxPage';
import BasketWithBagPage from './pages/BasketWithBagPage';
import FiltrationPartsPage from './pages/FiltrationPartsPage';
import SizeMyFilterPage from './pages/SizeMyFilterPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import TeamPage from './pages/TeamPage';
import BrandonPage from './pages/BrandonPage';
import AndrePage from './pages/AndrePage';
import JordanPage from './pages/JordanPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogPreviewPage from './pages/BlogPreviewPage';
import IndustriesOverviewPage from './pages/IndustriesOverviewPage';
import AgricultureIndustryPage from './pages/AgricultureIndustryPage';
import MunicipalIndustryPage from './pages/MunicipalIndustryPage';
import IndustrialIndustryPage from './pages/IndustrialIndustryPage';
import HydropowerIndustryPage from './pages/HydropowerIndustryPage';
import MiningIndustryPage from './pages/MiningIndustryPage';
import AquacultureIndustryPage from './pages/AquacultureIndustryPage';
import EnvironmentalIndustryPage from './pages/EnvironmentalIndustryPage';
import DesalinationIndustryPage from './pages/DesalinationIndustryPage';
import ShowLeadsPage from './pages/ShowLeadsPage';
import FloodResiliencePage from './pages/FloodResiliencePage';
import FloodIrrigationFiltrationPage from './pages/FloodIrrigationFiltrationPage';
import MunicipalFloodResilientIntakesPage from './pages/MunicipalFloodResilientIntakesPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminPostsPage from './pages/AdminPostsPage';
import AdminPostEditorPage from './pages/AdminPostEditorPage';
import AdminBannerSettingsPage from './pages/AdminBannerSettingsPage';
import ResearchPage from './pages/ResearchPage';
import InstantQuotePage from './pages/InstantQuotePage';
import ChemistryReviewPage from './pages/ChemistryReviewPage';
import { BlogProvider } from './contexts/BlogContext';
import { AuthProvider } from './contexts/AuthContext';
import WinterEffects from './components/WinterEffects';

// Component to detect mobile for homepage layout
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
}

// Component to handle scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AttributionTracker() {
  useAttributionPageView();
  return null;
}

function HomePage() {
  const isMobile = useIsMobile();
  
  return (
    <>
      <Navbar />
      <Hero />
      <CropShowcase />
      <SulfurBurners />
      <Hydrocoolers />
      <IrrigationFiltration />
      {/* Only show DPA Drenchers on desktop */}
      <Footer />
    </>
  );
}

function App() {
  const [isLoading, setIsLoading] = useState(true);
  
  // Handle GitHub Pages style routing fallback
  React.useEffect(() => {
    // Check if we're being redirected from a 404 page
    const search = window.location.search;
    if (search.includes('/?/')) {
      const path = search.replace('/?/', '/').replace(/&/g, '?').replace(/~and~/g, '&');
      window.history.replaceState(null, '', path);
    }
    
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ctc-blue"></div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <BlogProvider>
        <HelmetProvider>
          <Router>
            <div className="bg-white min-h-screen">
              {/* Winter Theme Effects */}
              <WinterEffects />

              {/* Newsletter Popup */}
              <NewsletterPopup />

              {/* Scroll to top on route changes */}
              <ScrollToTop />

              {/* Track page views for lead attribution */}
              <AttributionTracker />
              
              {/* HubSpot Tracking */}
              <HubSpotTracking portalId="243150835" />
              
              <Routes>
                <Route path="/" element={<HomePage />} />
              
              {/* Product Pages */}
              <Route path="/sulfur-burners" element={<SulfurBurnersPage />} />
              <Route path="/instant-quote" element={<InstantQuotePage />} />
              <Route path="/chemistry-review" element={<ChemistryReviewPage />} />
              <Route path="/hydrocoolers" element={<HydrocoolersPage />} />
              <Route path="/portable-hydrocoolers" element={<PortableHydrocoolersPage />} />
              <Route path="/dpa-drenchers" element={<DPADrenchersPage />} />
              <Route path="/filtration" element={<FiltrationLandingPage />} />
              <Route path="/filtration/inline" element={<InlineFiltersPage />} />
              <Route path="/filtration/inline/big" element={<BIGSeriesPage />} />
              <Route path="/filtration/inline/big-s" element={<BIGSSeriesPage />} />
              <Route path="/filtration/inline/gng" element={<GNGSeriesPage />} />
              <Route path="/filtration/inline/big/brush-cleaning" element={<BrushCleaningPage />} />
              <Route path="/filtration/inline/big/vacuum-cleaning" element={<VacuumCleaningPage />} />
              <Route path="/filtration/inline/cleaning" element={<CleaningOptionsPage />} />
              <Route path="/filtration/inline/cleaning/manual" element={<ManualCleaningPage />} />
              <Route path="/filtration/inline/cleaning/semi-auto" element={<SemiAutoCleaningPage />} />
              <Route path="/filtration/inline/cleaning/automatic" element={<FullyAutomaticCleaningPage />} />
              <Route path="/filtration/inline/tig" element={<TIGSeriesPage />} />
              <Route path="/filtration/self-cleaning" element={<SelfCleaningFiltersPage />} />
              <Route path="/filtration/suction-screens" element={<SuctionScreensPage />} />
              <Route path="/filtration/suction-screens/standard" element={<StandardSuctionScreensPage />} />
              <Route path="/filtration/suction-screens/scs2" element={<SelfCleaningSuctionScreensPage />} />
              <Route path="/filtration/suction-screens/self-cleaning" element={<SelfCleaningSuctionScreensPage />} />
              <Route path="/filtration/suction-screens/psf" element={<PassiveFishScreensPage />} />
              <Route path="/filtration/suction-screens/passive-fish-screens" element={<PassiveFishScreensPage />} />
              <Route path="/filtration/water-intake" element={<WaterIntakeLandingPage />} />
              <Route path="/filtration/water-intake/water-box" element={<WaterBoxPage />} />
              <Route path="/filtration/water-intake/basket-with-bag" element={<BasketWithBagPage />} />
              <Route path="/filtration/parts" element={<FiltrationPartsPage />} />
              <Route path="/filtration/size-my-filter" element={<SizeMyFilterPage />} />

              {/* Guide Pages */}
              <Route path="/sulfur-burner-guide" element={<SulfurBurnerGuidePage />} />
              <Route path="/tools/acid-cost-calculator" element={<AcidCostCalculatorPage />} />
              <Route path="/research" element={<ResearchPage />} />
              <Route path="/blueberry" element={<BlueberryPage />} />
              <Route path="/almond" element={<AlmondPage />} />
              <Route path="/cherry" element={<CherryPage />} />
              <Route path="/apricot" element={<ApricotPage />} />
              <Route path="/artichoke" element={<ArtichokePage />} />
              <Route path="/asparagus" element={<AsparagusPage />} />
              <Route path="/cannabis" element={<CannabisPage />} />
              <Route path="/apple" element={<ApplePage />} />
              <Route path="/hops" element={<HopsPage />} />
              <Route path="/grape" element={<GrapePage />} />
              <Route path="/pistachio" element={<PistachioPage />} />
              <Route path="/raspberry" element={<RaspberryPage />} />
              <Route path="/cherry-hydrocoolers" element={<CherryHydrocoolerPage />} />
              <Route path="/tomato" element={<TomatoPage />} />
              
              {/* Company Pages */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/team/brandon" element={<BrandonPage />} />
              <Route path="/team/andre" element={<AndrePage />} />
              <Route path="/team/jordan" element={<JordanPage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Team Member Short URL Redirects */}
              <Route path="/brandon" element={<Navigate to="/team/brandon" replace />} />
              <Route path="/andre" element={<Navigate to="/team/andre" replace />} />
              <Route path="/jordan" element={<Navigate to="/team/jordan" replace />} />
              
              {/* Blog Pages */}
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/blog/preview" element={<BlogPreviewPage />} />

              {/* Flood Resilience Pages */}
              <Route path="/flood-resilience" element={<FloodResiliencePage />} />
              <Route path="/flood-irrigation-filtration" element={<FloodIrrigationFiltrationPage />} />
              <Route path="/municipal/flood-resilient-intakes" element={<MunicipalFloodResilientIntakesPage />} />

              {/* Industries Pages */}
              <Route path="/industries" element={<IndustriesOverviewPage />} />
              <Route path="/industries/agriculture" element={<AgricultureIndustryPage />} />
              <Route path="/industries/municipal" element={<MunicipalIndustryPage />} />
              <Route path="/industries/industrial" element={<IndustrialIndustryPage />} />
              <Route path="/industries/hydropower" element={<HydropowerIndustryPage />} />
              <Route path="/industries/mining" element={<MiningIndustryPage />} />
              <Route path="/industries/aquaculture" element={<AquacultureIndustryPage />} />
              <Route path="/industries/environmental" element={<EnvironmentalIndustryPage />} />
              <Route path="/industries/desalination" element={<DesalinationIndustryPage />} />

              {/* Legal Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />

              {/* Internal Pages */}
              <Route path="/showleads" element={<ShowLeadsPage />} />

              {/* Admin Pages */}
              <Route path="/admin" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
              <Route path="/admin/posts" element={<AdminPostsPage />} />
              <Route path="/admin/posts/:id" element={<AdminPostEditorPage />} />
              <Route path="/admin/banner-settings" element={<AdminBannerSettingsPage />} />
            </Routes>
            </div>
          </Router>
        </HelmetProvider>
      </BlogProvider>
    </AuthProvider>
  );
}

export default App;